import React from 'react';
import { StepProps } from '../components/Step.component';
import { SlimRadioOption } from '../components/SlimRadioOption';
import { parseEngineVariables } from '../utils/engine-helper';
import { Option } from '../models/option';
import { BraSize, toBraSize, upsizeBraBand, upsizeBraCup } from '../utils/bra-helper';
import classNames from 'classnames';
import styles from './AdditionalBraSizeViewRender.module.scss';

enum Frequency {
  Yes,
  No,
}

interface AdditionalBraSizeViewRenderProps extends StepProps {
  onChange: (option: Option) => () => void;
  braKey: string;
  nextStepKey: string;
}

export const AdditionalBraSizeViewRender: React.FC<AdditionalBraSizeViewRenderProps> = ({ data, stepValues, braKey, onChange, nextStepKey, productHandle }) => {
  const preSelectedValue = stepValues.get(data.key);

  const [frequency, setFrequency] = React.useState<Frequency | undefined>(
    preSelectedValue === undefined ? undefined : preSelectedValue.text ? Frequency.Yes : Frequency.No,
  );
  const [userInteracted, setUserInteracted] = React.useState<boolean>(false);

  const braAnswer = stepValues.get(braKey);

  const bandUpsize = toBraSize(upsizeBraBand(braAnswer?.text as BraSize | undefined, productHandle));
  const cupUpsize = toBraSize(upsizeBraCup(braAnswer?.text as BraSize | undefined));

  const selectSize = React.useCallback(
    (size: BraSize | undefined) => {
      if (size) {
        onChange({
          text: size,
          nextStepKey: nextStepKey,
        })();
      }
    },
    [onChange, nextStepKey],
  );

  React.useEffect(() => {
    if (userInteracted) {
      if (frequency === Frequency.No) {
        onChange({
          text: 'no',
          nextStepKey: nextStepKey,
        })();
        return;
      }

      if (cupUpsize === undefined && bandUpsize === undefined) {
        // This should never happen, but adding it as a fallback
        selectSize(undefined);
        return;
      }
      if (cupUpsize === undefined) {
        selectSize(bandUpsize);
        return;
      }
      if (bandUpsize === undefined) {
        selectSize(cupUpsize);
        return;
      }
    }
  }, [frequency, userInteracted, bandUpsize, cupUpsize, nextStepKey, onChange, selectSize]);

  return (
    <div className={styles.stepContainer}>
      <div>
        <div className={classNames(styles.stepText, 'hide-desktop')}>
          <span className={styles.subText}>{parseEngineVariables(data.text, stepValues, productHandle, false)}</span>
        </div>
        <div className={classNames(styles.stepText, 'hide-mobile')} style={{ textAlign: 'center' }}>
          <span className={styles.subText}>{parseEngineVariables(data.text, stepValues, productHandle, false)}</span>
        </div>
        <SlimRadioOption
          key="no"
          onClick={() => {
            setUserInteracted(true);
            setFrequency(Frequency.No);
          }}
          isSelected={frequency === Frequency.No}
          dataKey={data.key}
          text="No"
        />
        <SlimRadioOption
          key="yes"
          onClick={() => {
            setUserInteracted(true);
            setFrequency(Frequency.Yes);
          }}
          isSelected={frequency === Frequency.Yes}
          dataKey={data.key}
          text="Yes"
        />
      </div>
      {frequency === Frequency.Yes && cupUpsize !== undefined && bandUpsize !== undefined && (
        <div className={styles.hlAdditionalBraExtended}>
          <div className={classNames('hide-desktop', styles.stepText)}>
            <span className={styles.subText}>{data.subText}</span>
          </div>
          <div className={classNames('hide-mobile', styles.stepText)} style={{ textAlign: 'center' }}>
            <span className={styles.subText}>{data.subText}</span>
          </div>
          <SlimRadioOption
            key="upsize-cup"
            onClick={() => cupUpsize !== undefined && selectSize(cupUpsize)}
            isSelected={preSelectedValue?.text === cupUpsize}
            dataKey={undefined}
            text={cupUpsize}
          />
          <SlimRadioOption
            key="upsize-band"
            onClick={() => selectSize(bandUpsize)}
            isSelected={preSelectedValue?.text === bandUpsize}
            dataKey={undefined}
            text={bandUpsize}
          />
        </div>
      )}
    </div>
  );
};

export default AdditionalBraSizeViewRender;
